"use client";

import * as React from "react";
import { Award, CircleCheck as CheckCircle2, Flame, Gauge, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
export interface ShopHeroProps {
  customHeading?: string;
  customDescription?: string;
}
export default function ShopHero({
  customHeading,
  customDescription
}: ShopHeroProps = {}) {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  React.useEffect(() => {
    try {
      const client = getWvcClient();
      const params = client && typeof client.getCurrentWPQueryParams === "function" ? client.getCurrentWPQueryParams() : {};
      const query = String(params?.s ?? params?.search ?? "").trim();
      setSearchTerm(query);
    } catch {
      setSearchTerm("");
    }
  }, []);
  const headingText = customHeading || (searchTerm ? `Search results for "${searchTerm}"` : "Residential Natural Gas Water Heaters");
  const descriptionText = customDescription || "Explore high-recovery storage tanks, tankless models, and specialty water heaters engineered for domestic heating efficiency and dependable performance.";
  return <section id="shop-hero" data-nav="light" className="relative border-b border-border bg-background text-foreground pt-10 pb-12 md:pt-16 md:pb-16">
      <div className="mx-auto max-w-[1240px] px-4 md:px-6">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Columns 1–8: Header & Description */}
          <div className="flex flex-col lg:col-span-8">
            {/* Signature Device: Datum line without regional references */}
            <div className="flex items-center gap-3 pb-3">
              <div className="flex items-center gap-1.5 text-primary">
                <Gauge className="size-4 stroke-[1.75]" />
                <span className="font-mono text-xs tracking-wider uppercase text-muted-foreground">
                  AQUAFLAME // Hardware Catalog
                </span>
              </div>
              <div className="h-px flex-1 bg-border" />
              <div className="size-1.5 rounded-full bg-secondary" />
            </div>

            {/* Eyebrow */}
            <div className="mb-2">
              <span className="font-serif text-xs font-bold tracking-[0.08em] text-primary uppercase">
                Direct Equipment Supply
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[2.75rem] lg:leading-[1.15]">
              {headingText}
            </h1>

            {/* Support Copy */}
            <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg leading-relaxed">
              {descriptionText}
            </p>
          </div>

          {/* Columns 9–12: Reassurance Card */}
          <div className="lg:col-span-4 lg:pt-2">
            <div className="rounded-md border border-border bg-card p-5 sm:p-6 shadow-xs transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div className="flex size-11 items-center justify-center rounded-md border border-border bg-muted text-primary">
                  <ShieldCheck className="size-5 stroke-[1.75]" />
                </div>
                <Badge variant="outline" className="rounded-md border-secondary/20 bg-secondary/10 px-2.5 py-1 text-xs font-bold text-secondary">
                  <Flame className="size-3 text-secondary mr-1" />
                  Reliable Hardware
                </Badge>
              </div>

              <div className="mt-4">
                <h2 className="font-serif text-base font-bold text-foreground">
                  Shop with confidence
                </h2>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                  Residential natural gas water heaters delivered directly to your doorstep with standard shipping across the United States.
                </p>
              </div>

              <div className="mt-4 space-y-2.5 border-t border-border pt-4">
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <CheckCircle2 className="size-4 shrink-0 text-primary stroke-[1.75]" />
                  <span>Ships across the United States</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <CheckCircle2 className="size-4 shrink-0 text-primary stroke-[1.75]" />
                  <span>14-day returns on unused, unopened items</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Award className="size-4 shrink-0 text-primary stroke-[1.75]" />
                  <span>Secure checkout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}